import { db } from "./db.js";
import { getMemberCaching, getCachedMemberRole } from "./teams.js";

// Foreign trademark matters — firms/{firmId}/additions/tm-foreign/trademarks.
// Never synced from USPTO; all writes come from the frontend (manual entry
// dialog or the spreadsheet import wizard). Doc ids are Firestore auto-ids —
// foreign application numbers are not normalized, so serialNumber is plain
// data, not the id.

function collection(firmId) {
  return db.collection(`firms/${firmId}/additions/tm-foreign/trademarks`);
}

// ==================== CREATE ====================

// Resolves to the auto-generated doc id so callers can attach related records
// (e.g. docketing tasks).
function createForeignTrademark(firmId, data) {
  const ref = collection(firmId).doc();
  return ref.set({
    alive: true,
    internationalClass: [],
    assigned: [],
    manuallyCreated: true,
    ...data,
  }).then(() => ref.id);
}

// ==================== READ ====================

// Role-aware stream (mirrors streamTrademarksOfUser): admins/managers stream
// the whole collection; members and clients stream only marks whose
// assigned[] contains their uid. Uses the cached role to attach instantly on
// repeat visits, re-checking membership in the background (a stale
// over-privileged role is denied by the rules, so this fails safe).
// Resolves to the unsubscribe function.
async function streamForeignTrademarksOfUser(firmId, userId, onSnap) {
  const collectionRef = collection(firmId);
  const queryFor = (role) =>
    role === "admin" || role === "manager"
      ? collectionRef
      : collectionRef.where("assigned", "array-contains", userId);
  const onError = (error) => { console.log(error); };

  const cachedRole = getCachedMemberRole(firmId, userId);
  if (cachedRole) {
    let unsubscribe = queryFor(cachedRole).onSnapshot(onSnap, onError);
    getMemberCaching(firmId, userId).then((member) => {
      const freshRole = (member && member.role) || "member";
      if (freshRole !== cachedRole) {
        unsubscribe();
        unsubscribe = queryFor(freshRole).onSnapshot(onSnap, onError);
      }
    });
    return () => unsubscribe();
  }

  const member = await getMemberCaching(firmId, userId);
  return queryFor((member && member.role) || "member").onSnapshot(onSnap, onError);
}

// One-shot existence probe (a single limit(1) read) so the store can skip
// the live foreign streams entirely when there is nothing to stream. Role
// scoping mirrors the stream: admin/manager probe the whole collection;
// members/clients probe their assigned[] scope because an unfiltered read
// would be denied by the rules.
async function hasForeignTrademarks(firmId, userId) {
  const member = await getMemberCaching(firmId, userId);
  const role = (member && member.role) || "member";
  const query =
    role === "admin" || role === "manager"
      ? collection(firmId).limit(1)
      : collection(firmId).where("assigned", "array-contains", userId).limit(1);
  const snapshot = await query.get();
  return !snapshot.empty;
}

async function getForeignTrademark(firmId, tmId) {
  const doc = await collection(firmId).doc(tmId).get();
  return doc.exists ? { id: doc.id, ...doc.data() } : null;
}

// NOTE: re-import matching in the spreadsheet wizard uses the
// country+serial+registration+mark tuple (see matterMatchKey in
// utils/trademarkImport.js), not this two-field query — kept for callers
// that predate the tuple key.
async function findForeignTrademark(firmId, jurisdiction, serialNumber) {
  const snapshot = await collection(firmId)
    .where("jurisdiction", "==", jurisdiction || "")
    .where("serialNumber", "==", serialNumber || "")
    .get();
  return snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

// ==================== UPDATE / DELETE ====================

function updateForeignTrademark(firmId, tmId, data, transaction = null) {
  const ref = collection(firmId).doc(tmId);
  if (transaction) {
    transaction.update(ref, data);
  } else {
    return ref.update(data);
  }
}

function deleteForeignTrademark(firmId, tmId) {
  return collection(firmId).doc(tmId).delete();
}

export {
  createForeignTrademark,
  hasForeignTrademarks,
  streamForeignTrademarksOfUser,
  getForeignTrademark,
  findForeignTrademark,
  updateForeignTrademark,
  deleteForeignTrademark,
};
